import React, { useMemo, useState } from "react";
import "./RenCostEstimator.css";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Bookmark, Building2, Download, Gauge, Home, Ruler, Zap } from "lucide-react";
import {
  SimulatorHeader,
  ReuseCard,
  FieldWithIcon,
  FieldLabel,
} from "../components/SimulatorParts";
import useSimulatorFormat from "../components/useSimulatorFormat";
import { downloadSimulationWorkbook } from "../utils/simulationExcel";
import {
  asText,
  readSimulationWorkbook,
} from "../utils/simulationExcelImport";
import {
  CURRENT_RATINGS,
  GIRONA_RENOVATION_PARAMS,
  PROPERTY_TYPES,
  estimateRenovationGirona,
} from "./RenCostEstimatorGironaCalc";

export default function RenCostEstimatorGirona({ onBack, onContinue }) {
  const { t, i18n } = useTranslation("renovation-cost-estimator-girona");
  const { money, wholeMoney, number } = useSimulatorFormat();

  const [propertyType, setPropertyType] = useState("apartment");
  const [floorArea, setFloorArea] = useState("");
  const [currentRating, setCurrentRating] = useState("E");
  const [currentConsumption, setCurrentConsumption] = useState("");

  const result = useMemo(
    () => estimateRenovationGirona({ propertyType, floorArea, currentRating, currentConsumption }),
    [propertyType, floorArea, currentRating, currentConsumption]
  );

  const kwh = (value) => t("kwh-value", { value: value.toFixed(1) });
  const projectedRange = result
    ? result.projected.conservative.rating === result.projected.standard.rating
      ? result.projected.conservative.rating
      : `${result.projected.conservative.rating} – ${result.projected.standard.rating}`
    : "—";

  const handleDownload = async () => {
    const currentInputs = {
      propertyType,
      floorArea,
      currentRating,
      currentConsumption,
    };

    const currentResult =
      estimateRenovationGirona(
        currentInputs,
      );

    if (!currentResult) {
      return;
    }

    await downloadSimulationWorkbook({
      simulationType: "RENOVATION_COST",
      region: "GIRONA",
      language:
        i18n.resolvedLanguage ||
        i18n.language ||
        "en",
      calculatorVersion:
        GIRONA_RENOVATION_PARAMS.version,
      inputs:
        currentInputs,
      results:
        currentResult,
    });
  };

  const handleApplyWorkbook = async (
    file,
  ) => {
    const { inputs } =
      await readSimulationWorkbook(
        file,
        {
          expectedSimulationType:
            "RENOVATION_COST",
          expectedRegion:
            "GIRONA",
        },
      );

    if (
      PROPERTY_TYPES.includes(
        inputs.propertyType,
      )
    ) {
      setPropertyType(
        inputs.propertyType,
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "floorArea",
      )
    ) {
      setFloorArea(
        asText(
          inputs.floorArea,
        ),
      );
    }

    if (
      CURRENT_RATINGS.includes(
        inputs.currentRating,
      )
    ) {
      setCurrentRating(
        inputs.currentRating,
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "currentConsumption",
      )
    ) {
      setCurrentConsumption(
        asText(
          inputs.currentConsumption,
        ),
      );
    }
  };

  return (
    <div className="simulator">
      {/* ---------- LEFT COLUMN ---------- */}
      <div className="simulator__left-col">
        <SimulatorHeader eyebrow={t("eyebrow")} title={t("title")} subtitle={t("subtitle")} />

        <ReuseCard
          title={t("reuse-title")}
          description={t("reuse-desc")}
          browseLabel={t("browse")}
          applyLabel={t("upload-apply")}
          onApply={handleApplyWorkbook}
        />

        <div className="simulator__card simulator__form-card">
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Home size={16} />
              <span>{t("your-home")}</span>
            </div>
            <button type="button" className="simulator__btn-outline simulator__btn-outline--sm">
              <Bookmark size={14} /> {t("load-saved")}
            </button>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field">
              <FieldLabel tip={t("tips.property-type")}>{t("property-type")}</FieldLabel>
              <FieldWithIcon icon={Building2}>
                <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
                  {PROPERTY_TYPES.map((type) => (
                    <option key={type} value={type}>{t(`property-types.${type}`)}</option>
                  ))}
                </select>
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.floor-area")}>{t("floor-area")}</FieldLabel>
              <FieldWithIcon icon={Ruler} unit="m²">
                <input
                  type="number"
                  min="0"
                  value={floorArea}
                  onChange={(e) => setFloorArea(e.target.value)}
                  placeholder="0"
                />
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.current-rating")}>{t("current-rating")}</FieldLabel>
              <FieldWithIcon icon={Gauge}>
                <select value={currentRating} onChange={(e) => setCurrentRating(e.target.value)}>
                  {CURRENT_RATINGS.map((rating) => (
                    <option key={rating} value={rating}>{rating}</option>
                  ))}
                </select>
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.current-consumption")}>{t("current-consumption")}</FieldLabel>
              <FieldWithIcon icon={Zap} unit="kWh/m²·yr">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={currentConsumption}
                  onChange={(e) => setCurrentConsumption(e.target.value)}
                  placeholder={t("optional")}
                />
              </FieldWithIcon>
            </div>
          </div>
          <p className="simulator__hint simulator__hint--after-grid">{t("rating-hint")}</p>

          <div className="simulator__footer">
            <button type="button" className="simulator__btn-back" onClick={onBack}>
              <ArrowLeft size={16} /> {t("back")}
            </button>
            <button type="button" className="simulator__btn-continue" onClick={onContinue}>
              {t("save-continue")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- RESULT ---------- */}
      <div className="simulator__card simulator__result-card">
        <div className="simulator__tags">
          <span className="simulator__tag">{t("tag-girona")}</span>
          <span className="simulator__tag">{t("tag-scope")}</span>
          <span className="simulator__tag simulator__tag--badge">{t("tag-indicative")}</span>
        </div>

        <label className="simulator__result-label">{t("budget-label")}</label>
        <span className="simulator__result-value">{result ? wholeMoney(result.budget) : "— €"}</span>

        {!result ? (
          <p className="simulator__empty-result">{t("empty-result")}</p>
        ) : (
          <>
            <div className="simulator__mini-cards">
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">{t("current-rating-short")}</span>
                <span className="simulator__mini-value">{result.currentRating}</span>
              </div>
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">{t("projected-rating")}</span>
                <span className="simulator__mini-value">{projectedRange}</span>
              </div>
            </div>

            <div className="simulator__rows">
              <div className="simulator__row">
                <span>{t("facade-area")}</span>
                <span>{t("m2-value", { value: result.facadeArea.toFixed(1) })}</span>
              </div>
              <div className="simulator__row">
                <span>{t("cost-per-m2")}</span>
                <span>{money(result.costPerM2)}</span>
              </div>
              <div className="simulator__row">
                <span>
                  {t("current-consumption-row")}
                  <span className="simulator__row-detail">
                    {result.fromCertificate ? t("from-certificate") : t("representative", { rating: result.currentRating })}
                  </span>
                </span>
                <span>{kwh(result.consumption)}</span>
              </div>
            </div>

            <div className="simulator__taxes-box">
              <span className="simulator__taxes-title">{t("after-works")}</span>
              {["conservative", "standard"].map((key) => (
                <div className="simulator__taxes-row" key={key}>
                  <span>
                    {t(`scenarios.${key}`)}
                    <span className="simulator__row-detail">
                      {t("saving", { value: number(result.projected[key].saving * 100) })}
                    </span>
                  </span>
                  <span>
                    {kwh(result.projected[key].consumption)} → <strong>{result.projected[key].rating}</strong>
                  </span>
                </div>
              ))}
              <p className="ren-cost__box-note">{t("scenarios-note")}</p>
            </div>

            {result.noChange && <p className="simulator__note">{t("no-change-note")}</p>}
            {result.certificateRating && result.certificateRating !== result.currentRating && (
              <p className="simulator__note simulator__note--warning">
                {t("certificate-mismatch", { rating: result.certificateRating, selected: result.currentRating })}
              </p>
            )}
            <p className="simulator__note">{t("scope-note")}</p>
          </>
        )}

        <p className="simulator__disclaimer">
          {t("disclaimer")}
          <br />
          {t("price-basis", { version: GIRONA_RENOVATION_PARAMS.version })}
        </p>

        <button
          type="button"
          className="simulator__btn-download"
          onClick={handleDownload}
          disabled={!result}
        >
          <Download size={16} /> {t("download")}
        </button>
      </div>
    </div>
  );
}
